/**
 * T037/T121: Sidebar Component
 * Navigation sidebar with mobile drawer support
 */

import { useEffect } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { ChevronLeft, FileText, Settings, Tags, FolderOpen, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUIStore } from '@/stores/ui'
import { cn } from '@/lib/utils'

const navItems = [
  {
    label: 'Prompts',
    href: '/prompts',
    icon: FileText,
  },
  {
    label: 'Categories',
    href: '/settings/categories',
    icon: FolderOpen,
  },
  {
    label: 'Tags',
    href: '/settings/tags',
    icon: Tags,
  },
  {
    label: 'Settings',
    href: '/settings',
    icon: Settings,
  },
]

function SidebarContent({
  collapsed,
  onNavClick,
  onToggle,
  showCollapseButton = true,
}: {
  collapsed: boolean
  onNavClick?: () => void
  onToggle?: () => void
  showCollapseButton?: boolean
}) {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  return (
    <div className="flex h-full flex-col">
      <nav className="flex-1 space-y-1 p-2">
        {navItems.map((item) => {
          // Use exact match for /settings to avoid highlighting when on /settings/categories or /settings/tags
          const isActive = item.href === '/settings'
            ? currentPath === item.href
            : currentPath === item.href || currentPath.startsWith(item.href + '/')
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavClick}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors min-h-[44px]',
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              title={collapsed ? item.label : undefined}
            >
              <item.icon className="h-5 w-5 shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </Link>
          )
        })}
      </nav>

      {showCollapseButton && onToggle && (
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-3 min-h-[44px]"
            onClick={onToggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft
              className={cn(
                'h-5 w-5 transition-transform',
                collapsed && 'rotate-180'
              )}
            />
            {!collapsed && <span>Collapse</span>}
          </Button>
        </div>
      )}
    </div>
  )
}

export function Sidebar() {
  const { sidebarCollapsed, toggleSidebar } = useUIStore()

  return (
    <aside
      className={cn(
        'fixed left-0 top-14 z-40 h-[calc(100vh-3.5rem)] border-r bg-background transition-all duration-200',
        'hidden md:block', // Hide on mobile
        sidebarCollapsed ? 'w-16' : 'w-64'
      )}
    >
      <SidebarContent
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />
    </aside>
  )
}

export function MobileSidebar() {
  const { isMobileMenuOpen, closeMobileMenu } = useUIStore()
  const routerState = useRouterState()

  // Close menu on route change
  useEffect(() => {
    closeMobileMenu()
  }, [routerState.location.pathname, closeMobileMenu])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  if (!isMobileMenuOpen) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside className="absolute left-0 top-0 h-full w-64 bg-background shadow-xl">
        <div className="flex h-14 items-center justify-between border-b px-4">
          <span className="text-lg font-semibold">Arcanum</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={closeMobileMenu}
            aria-label="Close menu"
            className="min-h-[44px] min-w-[44px]"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        <SidebarContent
          collapsed={false}
          onNavClick={closeMobileMenu}
          showCollapseButton={false}
        />
      </aside>
    </div>
  )
}
